import React, { useEffect, useState } from 'react';
import { Flex } from 'rebass';
import { Input, Label } from '../Form';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { useMutation } from '@apollo/client';
import useNotify from '../../hooks/useNotify';
import { AddressType } from './AddressPicker';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import StatePicker from './StatePicker';
import { gql } from '@/modes/common/gql';

const CREATE_ADDRESS = gql(`
  mutation CreateAddress($input: AddressInput!) {
    createAddress(input: $input) {
      id
      street1
      street2
      city
      state
      zip
      zipExt
      latitude
      longitude
    }
  }
`);

type ModalProps = {
  onSave?: (address: AddressType) => void;
  onClose?: () => void;
  userInput: string;
};

const AddAddressModal: React.FC<ModalProps> = ({
  onClose,
  onSave,
  userInput,
}) => {
  const [address, setAddress] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
  });

  const { showError } = useNotify();

  useEffect(() => {
    if (userInput) {
      setAddress((prevState) => ({ ...prevState, street1: userInput }));
    }
  }, []);

  const [
    createAddress,
    { data: createData, loading: createLoading, error: createError },
  ] = useMutation<{
    createAddress: AddressType;
  }>(CREATE_ADDRESS, {
    onCompleted: (data) => {
      if (data.createAddress) {
        onSave?.(data.createAddress);
        onClose?.();
      }
    },
    onError: (error) => {
      showError('Error creating address', error);
      console.error('Error creating address', error);
    },
  });

  const handleAddressChange = (e: any) => {
    const { name, value } = e.target;
    setAddress((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  return (
    <Modal title="Add Address" onClose={onClose}>
      <ModalBody>
        <Flex flexDirection="column" m={3}>
          <Label>Street 1</Label>
          <Input
            autoFocus
            width="100%"
            type="text"
            value={address.street1}
            onChange={handleAddressChange}
            name="street1"
            autoComplete="off"
          />
          <Label mt={3}>Street 2</Label>
          <Input
            width="100%"
            type="text"
            value={address.street2}
            onChange={handleAddressChange}
            name="street2"
            autoComplete="off"
          />
          <Label mt={3}>City</Label>
          <Input
            width="100%"
            type="text"
            value={address.city}
            onChange={handleAddressChange}
            name="city"
          />
          <FormRow>
            <FormColumn>
              <Label>State</Label>
              <StatePicker
                width="100%"
                value={address.state}
                onChange={handleAddressChange}
                name="state"
              />
            </FormColumn>
            <FormColumn>
              <Label>Zip</Label>
              <Input
                width="100%"
                type="text"
                value={address.zip}
                onChange={handleAddressChange}
                name="zip"
                autoComplete="off"
              />
            </FormColumn>
          </FormRow>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="save"
          disabled={
            createLoading ||
            !address.street1 ||
            !address.city ||
            !address.state ||
            !address.zip
          }
          onClick={() =>
            createAddress({
              variables: {
                input: address,
              },
            })
          }
        >
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddAddressModal;
